/**
 * Hooks for the human expert review feature (entitlement, list, detail, mutations).
 *
 * @module useHumanReviews
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelReviewRequest,
  createReviewRequest,
  getEntitlement,
  getReviewRequestDetail,
  getReviewRequests,
  getReviewSummary,
  type CreateReviewPayload,
  type Entitlement,
  type ReviewDetail,
  type ReviewListItem,
  type ReviewSummary,
} from '@/lib/humanReviewsApi';
import { useAuth } from '@/hooks/useAuth';

export const ENTITLEMENT_QUERY_KEY = ['humanReviewEntitlement'] as const;
export const REVIEW_SUMMARY_QUERY_KEY = ['humanReviewSummary'] as const;
export const REVIEW_LIST_QUERY_KEY = ['humanReviewList'] as const;
export const REVIEW_DETAIL_QUERY_KEY = ['humanReviewDetail'] as const;

const scopedQueryKey = (prefix: readonly string[], userId: string | null | undefined) =>
  userId ? ([...prefix, userId] as const) : prefix;

export const humanReviewEntitlementQueryKey = (userId: string | null | undefined) =>
  scopedQueryKey(ENTITLEMENT_QUERY_KEY, userId);

const scopedDetailQueryKey = (
  userId: string | null | undefined,
  id: string | null,
) =>
  userId
    ? ([...REVIEW_DETAIL_QUERY_KEY, userId, id] as const)
    : ([...REVIEW_DETAIL_QUERY_KEY, id] as const);

export function useEntitlement(enabled: boolean = true) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { data, isLoading, isError, refetch } = useQuery<Entitlement | null>({
    queryKey: humanReviewEntitlementQueryKey(userId),
    queryFn: getEntitlement,
    enabled: enabled && Boolean(userId),
    staleTime: 0,
  });
  return { entitlement: data ?? null, isLoading, isError, refetch };
}

export function useReviewSummary(enabled: boolean = true) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { data, isLoading, isError, refetch } = useQuery<ReviewSummary | null>({
    queryKey: scopedQueryKey(REVIEW_SUMMARY_QUERY_KEY, userId),
    queryFn: getReviewSummary,
    enabled: enabled && Boolean(userId),
    staleTime: 0,
  });
  return { summary: data ?? null, isLoading, isError, refetch };
}

export function useReviewRequests(enabled: boolean = true) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { data, isLoading, isError, refetch } = useQuery<ReviewListItem[]>({
    queryKey: scopedQueryKey(REVIEW_LIST_QUERY_KEY, userId),
    queryFn: getReviewRequests,
    enabled: enabled && Boolean(userId),
    staleTime: 0,
  });
  return { requests: data ?? [], isLoading, isError, refetch };
}

export function useReviewRequestDetail(id: string | null) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { data, isLoading, isError, refetch } = useQuery<ReviewDetail | null>({
    queryKey: scopedDetailQueryKey(userId, id),
    queryFn: () => (id ? getReviewRequestDetail(id) : Promise.resolve(null)),
    enabled: Boolean(id) && Boolean(userId),
    staleTime: 0,
  });
  return { detail: data ?? null, isLoading, isError, refetch };
}

export function useCreateReviewRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateReviewPayload) => createReviewRequest(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ENTITLEMENT_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: REVIEW_LIST_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: REVIEW_SUMMARY_QUERY_KEY });
    },
  });
}

export function useCancelReviewRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelReviewRequest(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ENTITLEMENT_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: REVIEW_LIST_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: REVIEW_SUMMARY_QUERY_KEY });
    },
  });
}
